import { useState, useLayoutEffect, useMemo } from 'react';
import type { SrpTopRolesStripItem } from '../types/srp-top-roles-strip';
import { loadTopRolesStripDeduped } from '../utils/srp/fetch-top-roles-strip-api';

function stripSeedKey(initial: SrpTopRolesStripItem[] | undefined | null): string {
  if (!Array.isArray(initial) || initial.length === 0) return '';
  return initial.map((r) => r.slug).join('|');
}

export interface TopRolesStripData {
  roles: SrpTopRolesStripItem[];
  /** True while waiting for client fetch (no SSR seed). */
  isPending: boolean;
}

export interface UseTopRolesStripDataOptions {
  /** When false, keep the strip empty and do not call `/api/top-roles-strip` (e.g. location header pages). */
  allowClientFetch?: boolean;
}

/**
 * Uses SSR seed when non-empty; otherwise fetches `/api/top-roles-strip` (client navigation / slim payload).
 * Uses useLayoutEffect so the request starts before useEffect-driven Algolia search, and deduped loading
 * so the strip shares one in-flight request with other callers (e.g. progressive SRP island).
 */
export function useTopRolesStripData(
  initial: SrpTopRolesStripItem[] | undefined | null,
  options?: UseTopRolesStripDataOptions
): TopRolesStripData {
  const allowClientFetch = options?.allowClientFetch !== false;
  const seedKey = useMemo(() => stripSeedKey(initial), [initial]);

  const [items, setItems] = useState<SrpTopRolesStripItem[]>(() => {
    if (!allowClientFetch) return [];
    return Array.isArray(initial) && initial.length > 0 ? initial : [];
  });
  const [fetchDone, setFetchDone] = useState(() => !allowClientFetch || Boolean(stripSeedKey(initial)));

  useLayoutEffect(() => {
    let cancelled = false;

    if (!allowClientFetch) {
      setItems([]);
      setFetchDone(true);
      return () => {
        cancelled = true;
      };
    }

    if (seedKey) {
      if (Array.isArray(initial) && initial.length > 0) {
        setItems(initial);
      }
      setFetchDone(true);
      return () => {
        cancelled = true;
      };
    }

    setFetchDone(false);
    loadTopRolesStripDeduped().then((next) => {
      if (cancelled) return;
      if (next.length > 0) setItems(next);
      setFetchDone(true);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seedKey encodes list identity; avoid refetch when parent recreates the same array
  }, [seedKey, allowClientFetch]);

  const isPending = allowClientFetch && !seedKey && !fetchDone;

  return { roles: items, isPending };
}
